import { PageControls } from './JobWorkspace';
import React, { useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Typography } from '@mui/joy';
import { Eye as ViewIcon, Plus as PlusIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ordersApi, type Order } from '../../lib/distributorsApi';
import { ResponsiveTable, type ResponsiveColumn } from '../common/ResponsiveTable';
import OrderDetailModal from '../distributors/OrderDetailModal';

const STATUS_COLORS: Record<string, 'neutral' | 'primary' | 'warning' | 'success' | 'danger'> = {
  draft: 'neutral',
  submitted: 'primary',
  processing: 'warning',
  completed: 'success',
  cancelled: 'danger',
};

/**
 * Material Orders tab on the Job detail page: distributor orders linked to
 * this job, plus a deep link into the Distributors order builder with the
 * job preselected (?tab=create&job=N — prefills the delivery address).
 */
export function JobMaterialOrdersTab({ jobId }: { jobId: string }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  React.useEffect(() => { setPage(1); }, [jobId]);
  const [viewingOrder, setViewingOrder] = useState<Order | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['job-material-orders', jobId, page],
    queryFn: () => ordersApi.list({ job: Number(jobId), page }),
  });

  const orders = data?.results || [];

  const columns: ResponsiveColumn<Order>[] = [
    {
      key: 'order_number',
      sortValue: (row) => row.order_number,
      header: 'Order #',
      mobileVariant: 'header',
      render: (order) => (
        <Box>
          <Typography level="title-sm">{order.order_number}</Typography>
          {order.po_number && (
            <Typography level="body-xs" color="neutral">PO {order.po_number}</Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'date',
      sortValue: (row) => row.created_at ? new Date(row.created_at) : null,
      header: 'Date',
      render: (order) => (
        <Typography level="body-sm">
          {new Date(order.created_at).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      key: 'distributor',
      sortValue: (row) => row.distributor_name,
      header: 'Distributor',
      render: (order) => (
        <Typography level="body-sm">
          {order.distributor_name || order.distributor_details?.name || '—'}
        </Typography>
      ),
    },
    {
      key: 'total',
      sortValue: (row) => Number(row.total_amount),
      header: 'Total',
      render: (order) => <Typography level="body-sm">${order.total_amount}</Typography>,
    },
    {
      key: 'status',
      sortValue: (row) => row.status,
      header: 'Status',
      render: (order) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip size="sm" color={STATUS_COLORS[order.status] || 'neutral'}>
            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
          </Chip>
          {order.external_status && (
            <Chip size="sm" variant="outlined" color="neutral">{order.external_status}</Chip>
          )}
        </Box>
      ),
    },
    {
      key: 'method',
      sortValue: (row) => row.submission_method,
      header: 'Method',
      render: (order) => (
        <Chip size="sm" variant="soft" color={order.submission_method === 'api' ? 'primary' : 'neutral'}>
          {order.submission_method === 'api' ? 'API' : 'Email'}
        </Chip>
      ),
    },
    {
      key: 'confirmation',
      sortValue: (row) => row.confirmation_number,
      header: 'Confirmation #',
      hideOnMobile: true,
      render: (order) => (
        <Typography level="body-sm">{order.confirmation_number || '—'}</Typography>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography level="h4">Material Orders</Typography>
        <Button
          size="sm"
          startDecorator={<PlusIcon />}
          onClick={() => navigate(`/operations/distributors?tab=create&job=${jobId}`)}
        >
          New order
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert color="danger" endDecorator={<Button onClick={() => { if (page > 1) setPage(1); else refetch(); }}>Reload orders</Button>}>Failed to load material orders.</Alert>
      ) : (
        <><PageControls page={page} count={data?.count || 0} hasNext={!!data?.next} onChange={setPage} label="material orders" />
        <ResponsiveTable<Order>
          rows={orders}
          getRowKey={(order) => order.id}
          columns={columns}
          rowActions={(order) => (
            <IconButton
              size="sm"
              variant="outlined"
              onClick={() => setViewingOrder(order)}
              title="View order"
            >
              <ViewIcon />
            </IconButton>
          )}
          emptyState={
            <Alert color="neutral">
              No material orders linked to this job yet — press "New order" to start
              one with this job's delivery address prefilled.
            </Alert>
          }
        />
        </>
      )}

      <OrderDetailModal
        open={!!viewingOrder}
        onClose={() => setViewingOrder(null)}
        order={viewingOrder}
        onRefresh={() => refetch()}
      />
    </Box>
  );
}
